import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { LogoLink } from "@/components/brand/Logo";
import { CTAButton } from "@/components/common/CTA";
import { nav } from "@/config/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-40 bg-[#F6F7F5]/95 backdrop-blur-md border-b transition-colors duration-300 ${
        scrolled ? "border-[#D9E0E4]" : "border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-page flex items-center justify-between h-[68px]">
        <LogoLink />

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {nav.map((item) => {
            const active = location.pathname === item.path ||
              (item.children && item.children.some((c) => c.path === location.pathname));

            if (item.children) {
              return (
                <div key={item.label} className="relative group">
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative inline-flex items-center gap-1 text-[14.5px] font-medium transition-colors ${
                      active ? "text-teal" : "text-navy hover:text-teal"
                    }`}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" aria-hidden="true" />
                    {active && <span className="absolute -bottom-1 left-0 h-[2px] w-[calc(100%-16px)] bg-copper rounded-full" />}
                  </Link>
                  <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200 z-50">
                    <div className="w-64 rounded-lg border border-[#D9E0E4] bg-white shadow-xl p-2">
                      {item.children.map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`block rounded-md px-3 py-2.5 text-[14px] transition-colors ${
                            location.pathname === c.path ? "bg-[#EAF1F4] text-teal font-semibold" : "text-navy hover:bg-[#EAF1F4] hover:text-teal"
                          }`}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative text-[14.5px] font-medium transition-colors ${
                  active ? "text-teal" : "text-navy hover:text-teal"
                }`}
              >
                {item.label}
                {active && (
                  <span className="absolute -bottom-1 left-0 h-[2px] w-full bg-copper rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <CTAButton
            to="/request-a-quote"
            variant="primary"
            className="hidden sm:inline-flex px-5 min-h-[44px]"
            data-testid="header-request-quote"
            icon={ArrowRight}
          >
            Request a quote
          </CTAButton>
          <button
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md border border-[#D9E0E4] text-navy"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="fixed top-0 right-0 z-50 h-full w-[86%] max-w-sm bg-[#F6F7F5] shadow-2xl lg:hidden flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mobile-menu-panel"
            >
              <div className="flex items-center justify-between h-[68px] px-5 border-b border-[#D9E0E4]">
                <LogoLink size={34} />
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-[#D9E0E4] text-navy"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 py-6 flex flex-col gap-1" aria-label="Mobile">
                {nav.map((item) => (
                  <div key={item.label} className="border-b border-[#E3E9EC]">
                    <Link
                      to={item.path}
                      className="py-3.5 text-[18px] font-heading font-semibold text-navy flex items-center justify-between"
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.label}
                      <ArrowRight className="h-4 w-4 text-teal" />
                    </Link>
                    {item.children && (
                      <div className="pb-3 pl-3 flex flex-col">
                        {item.children.map((c) => (
                          <Link
                            key={c.path}
                            to={c.path}
                            className="py-2 text-[15px] text-muted-foreground hover:text-teal flex items-center gap-2"
                            data-testid={`mobile-nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <span className="h-1 w-1 rounded-full bg-copper" />
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
              <div className="p-5 border-t border-[#D9E0E4]">
                <CTAButton to="/request-a-quote" variant="primary" className="w-full" data-testid="mobile-request-quote">
                  Request a sourcing quote
                </CTAButton>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
